import { useEffect, useRef, useState } from "react";
import { RotateCcw } from "lucide-react";
import { COLOR_FIELDS, DISPLAY_FONTS, BODY_FONTS, DEFAULT_DISPLAY, DEFAULT_BODY } from "@/lib/design";

const ColorRow = ({ k, label, def, value, onChange }) => {
  const [v, setV] = useState(value || def);
  const timer = useRef(null);
  useEffect(() => { setV(value || def); }, [value, def]);
  useEffect(() => () => clearTimeout(timer.current), []);
  const change = (nv) => {
    setV(nv);
    clearTimeout(timer.current);
    if (/^#[0-9a-fA-F]{6}$/.test(nv)) timer.current = setTimeout(() => onChange(nv), 350);
  };
  return (
    <div className="flex items-center gap-3 py-1.5">
      <input type="color" data-testid={`ve-color-${k}`} value={/^#[0-9a-fA-F]{6}$/.test(v) ? v : def} onChange={(e) => change(e.target.value)} className="h-9 w-11 shrink-0 cursor-pointer rounded-lg border border-[#14213D]/15 bg-transparent p-0.5" />
      <div className="min-w-0 flex-1">
        <p className="truncate font-ui text-xs font-semibold text-[#14213D]">{label}</p>
        <input data-testid={`ve-color-hex-${k}`} value={v} onChange={(e) => change(e.target.value)} className="w-24 bg-transparent font-mono text-[11px] uppercase text-[#4A5568] outline-none" />
      </div>
      {value && value !== def && <button type="button" data-testid={`ve-color-reset-${k}`} onClick={() => onChange("")} className="rounded-full p-1.5 text-[#5F6875] hover:bg-[#14213D]/5" aria-label="Reset color"><RotateCcw className="h-3.5 w-3.5" /></button>}
    </div>
  );
};

const FontSelect = ({ testid, label, list, value, def, onChange }) => (
  <label className="block py-1.5">
    <span className="font-ui text-xs font-semibold text-[#14213D]">{label}</span>
    <select data-testid={testid} value={value || def} onChange={(e) => onChange(e.target.value === def ? "" : e.target.value)} className="mt-1 w-full rounded-xl border border-[#14213D]/15 bg-white px-3 py-2 text-sm text-[#14213D] outline-none">
      {list.map((f) => <option key={f} value={f} style={{ fontFamily: f }}>{f}{f === def ? " (original)" : ""}</option>)}
    </select>
  </label>
);

export const ColorsFonts = ({ theme = {}, onTheme }) => {
  const size = Number(theme.base_size) || 16;
  return (
    <div className="grid gap-5">
      <div>
        <p className="mb-1 font-ui text-[11px] uppercase tracking-widest text-[#8A6A12]">Colors</p>
        {COLOR_FIELDS.map(([k, label, def]) => <ColorRow key={k} k={k} label={label} def={def} value={theme[k]} onChange={(v) => onTheme({ [k]: v })} />)}
      </div>
      <div>
        <p className="mb-1 font-ui text-[11px] uppercase tracking-widest text-[#8A6A12]">Fonts</p>
        <FontSelect testid="ve-font-display" label="Headings font" list={DISPLAY_FONTS} def={DEFAULT_DISPLAY} value={theme.font_display} onChange={(v) => onTheme({ font_display: v })} />
        <FontSelect testid="ve-font-body" label="Body & buttons font" list={BODY_FONTS} def={DEFAULT_BODY} value={theme.font_body} onChange={(v) => onTheme({ font_body: v })} />
        <label className="block py-1.5">
          <span className="flex items-center justify-between font-ui text-xs font-semibold text-[#14213D]">Text size <span data-testid="ve-size-value" className="font-mono text-[11px] text-[#4A5568]">{size}px{size === 16 ? " (original)" : ""}</span></span>
          <input type="range" data-testid="ve-size-slider" min={14} max={20} step={1} value={size} onChange={(e) => onTheme({ base_size: Number(e.target.value) === 16 ? "" : Number(e.target.value) })} className="mt-2 w-full accent-[#C8862F]" />
          <span className="flex justify-between text-[10px] text-[#5F6875]"><span>Smaller</span><span>Bigger</span></span>
        </label>
      </div>
    </div>
  );
};
